/**
 * Remoción de fondo 100% client-side con briaai/RMBG-1.4 vía Transformers.js.
 * Basado en el ejemplo oficial https://huggingface.co/spaces/Xenova/remove-background-web
 *
 * El modelo (~44 MB cuantizado) se descarga una sola vez y queda cacheado por
 * el navegador (Cache Storage). `preloadModel()` se dispara en paralelo al
 * fetch de /init para que la espera ocurra durante la pantalla de engagement.
 */
import { AutoModel, AutoProcessor, env, RawImage } from '@xenova/transformers';
import type { PreTrainedModel, Processor } from '@xenova/transformers';

// Solo modelos remotos del Hub (no hay servidor propio de modelos)
env.allowLocalModels = false;

// Ejecuta WASM en un worker para no bloquear el main thread del sitio anfitrión
if (env.backends?.onnx?.wasm) {
	env.backends.onnx.wasm.proxy = true;
}

const MODEL_ID = 'briaai/RMBG-1.4';

let modelPromise: Promise<{ model: PreTrainedModel; processor: Processor }> | null = null;

export interface ModelProgress {
	status: string;
	progress?: number;
	file?: string;
}

export function preloadModel(onProgress?: (p: ModelProgress) => void) {
	modelPromise ??= (async () => {
		const model = await AutoModel.from_pretrained(MODEL_ID, {
			// RMBG-1.4 no publica config estándar: se fuerza el tipo custom
			config: { model_type: 'custom' },
			progress_callback: onProgress
		});

		const processor = await AutoProcessor.from_pretrained(MODEL_ID, {
			config: {
				do_normalize: true,
				do_pad: false,
				do_rescale: true,
				do_resize: true,
				image_mean: [0.5, 0.5, 0.5],
				feature_extractor_type: 'ImageFeatureExtractor',
				image_std: [1, 1, 1],
				resample: 2,
				rescale_factor: 0.00392156862745098,
				size: { width: 1024, height: 1024 }
			}
		});

		return { model, processor };
	})();

	return modelPromise;
}

/**
 * Segmenta la selfie y devuelve un canvas RGBA con el fondo transparente.
 * Todo ocurre en el navegador: la imagen nunca sale del dispositivo.
 */
export async function removeBackground(imageDataUrl: string): Promise<HTMLCanvasElement> {
	const { model, processor } = await preloadModel();

	const image = await RawImage.fromURL(imageDataUrl);

	// Preprocesa (resize 1024x1024, normaliza) y corre la inferencia
	const { pixel_values } = await processor(image);
	const { output } = await model({ input: pixel_values });

	// La salida es la máscara alpha: se reescala al tamaño original
	const mask = await RawImage.fromTensor(output[0].mul(255).to('uint8')).resize(
		image.width,
		image.height
	);

	const canvas = document.createElement('canvas');
	canvas.width = image.width;
	canvas.height = image.height;
	const ctx = canvas.getContext('2d')!;

	ctx.drawImage(image.toCanvas(), 0, 0);

	// Aplica la máscara al canal alfa píxel a píxel
	const pixelData = ctx.getImageData(0, 0, image.width, image.height);
	for (let i = 0; i < mask.data.length; ++i) {
		pixelData.data[4 * i + 3] = mask.data[i];
	}
	ctx.putImageData(pixelData, 0, 0);

	return canvas;
}
